import React from 'react';
import { Box, MenuItem, Select, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { getScrollEdgeFadeSx, useScrollEdgeFade } from '../../hooks/useScrollEdgeFade';
import { leadsSelectMenuProps } from '../../Leads/components/leadsUi.styles';
import MetaPeriodSelect from './MetaPeriodSelect';
import {
  metaAdsMutedTextSx,
  metaCampaignsHeaderSx,
  metaCampaignsLayoutSx,
  metaCampaignsListSx,
  metaPeriodSelectSx,
  metaSectionControlsSx,
  metaSectionControlsWithScopeSx,
  metaSectionFreshnessSlotSx,
  metaSectionTopRowSx,
  metaViewToggleSx,
} from './metaAdsUi.styles';

/**
 * Estructura común de Análisis, Presupuesto e Informe: encabezado fijo (frescura + ⚙, campaña y
 * período, y después el toggle opcional: primero se elige la campaña, después qué mirar) y una
 * lista que es la única zona que scrollea (scroll de Terrio + fade). Mismo chrome que Campañas.
 *
 * `scope` = `{ campaigns: [{ id, name }], value, onChange }` o null (Informe es de todo el espacio).
 * `toggle` = `{ options: [{ key, label }], value, onChange, ariaLabel }` o null.
 */
export default function MetaSectionLayout({
  testId,
  freshness = '',
  headerTools = null,
  periodPreset,
  onPeriodPresetChange,
  scope = null,
  toggle = null,
  listNotices = null,
  children,
}) {
  const { scrollRef, fadeTop, fadeBottom } = useScrollEdgeFade();
  const scopeValue = scope && scope.campaigns.some((c) => c.id === scope.value) ? scope.value : '';
  return (
    <Box sx={metaCampaignsLayoutSx} data-testid={`${testId}-layout`}>
      <Box sx={metaCampaignsHeaderSx} data-testid={`${testId}-header`} role="toolbar" aria-label="Filtros">
        {/* t1056u: frescura a la izquierda y controles (campaña, período) a la derecha en la misma
            fila, centrados. Si no entran (panel angosto), los controles bajan a una segunda línea
            alineada a la derecha: nada se trunca. */}
        <Box sx={metaSectionTopRowSx} data-testid={`${testId}-top-row`}>
          <Box sx={metaSectionFreshnessSlotSx}>
            {freshness ? (
              <Typography noWrap sx={{ ...metaAdsMutedTextSx, fontSize: '12px' }} data-testid={`${testId}-freshness`}>{freshness}</Typography>
            ) : null}
          </Box>
          {headerTools}
          <Box sx={scope ? metaSectionControlsWithScopeSx : metaSectionControlsSx} data-testid={`${testId}-filters`}>
            {scope ? (
              <Select
                size="small"
                displayEmpty
                value={scopeValue}
                onChange={(e) => scope.onChange(e.target.value)}
                inputProps={{ 'aria-label': 'Campaña' }}
                MenuProps={leadsSelectMenuProps}
                sx={{ ...metaPeriodSelectSx, flex: 1, minWidth: 0, '& .MuiSelect-select': { ...metaPeriodSelectSx['& .MuiSelect-select'], overflow: 'hidden', textOverflow: 'ellipsis' } }}
                data-testid="meta-scope-select"
              >
                <MenuItem value="">Todas las campañas</MenuItem>
                {scope.campaigns.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </Select>
            ) : null}
            <MetaPeriodSelect value={periodPreset} onChange={onPeriodPresetChange} testId="meta-period-select" />
          </Box>
        </Box>
        {toggle ? (
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={toggle.value}
            onChange={(_e, next) => { if (next) toggle.onChange(next); }}
            aria-label={toggle.ariaLabel}
            sx={metaViewToggleSx}
            data-testid={`${testId}-toggle`}
          >
            {toggle.options.map((o) => (
              <ToggleButton key={o.key} value={o.key} data-testid={`${testId}-toggle-${o.key}`}>{o.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        ) : null}
      </Box>
      <Box
        ref={scrollRef}
        sx={{ ...metaCampaignsListSx, ...getScrollEdgeFadeSx(fadeTop, fadeBottom) }}
        data-testid={`${testId}-list`}
      >
        {listNotices}
        {children}
      </Box>
    </Box>
  );
}
